/**
 * Session files (ADR 0004): one JSON file per session, `<sessionId>.json`.
 *
 * `pnpm sessions:export` writes them from the sheet; the operator copies the ones a machine
 * runs into its sessions folder, and the graphics admin's editor rewrites them (people and
 * session text). Strict: a misspelled or unknown key fails loudly.
 *
 * Speaker order is the array order, so "two hosts" and "order used twice" can't be written.
 * Participant IDs are not stored here; the graphics app generates them (SPEC §6).
 */
import { z } from 'zod';
import { MAX_PARTICIPANTS, normalizeName, SessionSchema } from './session.ts';

export const SESSION_FILE_SCHEMA_VERSION = 1;

/** A session ID that is also a safe file name on Windows (no path separators, no reserved characters). */
export const SESSION_FILE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

/** One person: the host or a speaker. */
export const SessionFilePersonSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  organization: z.string().max(200),
  /** The person's job title (the sheet's `title` column). */
  jobTitle: z.string().max(200),
});
export type SessionFilePerson = z.infer<typeof SessionFilePersonSchema>;

export const SessionFileSchema = z
  .strictObject({
    schemaVersion: z.literal(SESSION_FILE_SCHEMA_VERSION),
    session: SessionSchema,
    /** `null` when the session has no host. */
    host: SessionFilePersonSchema.nullable(),
    /** Speakers in order: the first is `order` 1 (CAM-2), and so on. */
    speakers: z.array(SessionFilePersonSchema).max(MAX_PARTICIPANTS),
    /** When `pnpm sessions:export` wrote the file. */
    exportedAt: z.iso.datetime({ offset: true }),
    /** Set by the admin editor on every save. */
    editedAt: z.iso.datetime({ offset: true }).optional(),
  })
  .superRefine((f, ctx) => {
    const people = [...(f.host ? [f.host] : []), ...f.speakers];
    if (!SESSION_FILE_ID.test(f.session.sessionId)) {
      ctx.addIssue({ code: 'custom', path: ['session', 'sessionId'], message: 'must be letters, digits, ".", "_" or "-" (it is also the file name)' });
    }
    if (people.length > MAX_PARTICIPANTS) {
      ctx.addIssue({ code: 'custom', path: ['speakers'], message: `${people.length} people; at most ${MAX_PARTICIPANTS} participants (host included)` });
    }
    if (f.session.type === 'break' && people.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['speakers'], message: 'a break has no host or speakers' });
    }
    const seen = new Set<string>();
    for (const p of people) {
      const key = normalizeName(p.name);
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: ['speakers'], message: `"${p.name}" appears twice; names must be unique within a session` });
      seen.add(key);
    }
  });
export type SessionFile = z.infer<typeof SessionFileSchema>;

/** The file name a session is stored under. */
export function sessionFileName(sessionId: string): string {
  return `${sessionId}.json`;
}
